import { Track, UserSettings, DesignTheme, StreamQualityPref } from '../types';
import { tg } from './telegram';
import { audioEngine } from './audioEngine';
import { sanitizeTrackStream } from './monochromeApi';

type StoreListener = () => void;

const LEGACY_UNSCOPED_KEYS = [
  'musictv_favorites',
  'musictv_history',
  'musictv_queue',
  'musictv_settings',
  'musictv_recent_searches',
  'musictv:favorites',
  'musictv:history',
  'musictv:queue',
  'musictv:settings',
  'musictv:recent_searches',
  'favorites',
  'history',
  'queue',
];

function scopedKey(scope: string, key: 'favorites' | 'history' | 'queue' | 'settings' | 'recent_searches'): string {
  return `musictv:${scope}:${key}`;
}

const DEFAULT_SETTINGS: UserSettings = {
  theme: 'obsidian-liquid',
  streamQuality: 'hires',
  hapticsEnabled: true,
  autoPlayNext: true,
  eqPreset: 'flat',
};

export const EQ_PRESETS = {
  flat: { label: 'Линейный (0 дБ)', bass: 0, treble: 0 },
  bass: { label: 'Глубокий бас (+6 дБ)', bass: 6, treble: 1 },
  vocal: { label: 'Чистый вокал', bass: -2, treble: 5 },
  warm: { label: 'Тёплая лента', bass: 4, treble: -2 },
  custom: { label: 'Пользовательский', bass: 0, treble: 0 },
} as const;

export const STREAM_QUALITY_OPTIONS = [
  {
    id: 'hires' as StreamQualityPref,
    label: 'Студийный мастер (24-бит / до 192 кГц FLAC)',
    shortLabel: '24-BIT FLAC',
    badge: 'HI-RES',
    desc: 'Прямой мастеринг-поток без сжатия из архивов Qobuz и Tidal Master',
  },
  {
    id: 'cd' as StreamQualityPref,
    label: 'CD Lossless (16-бит / 44.1 кГц FLAC)',
    shortLabel: '16-BIT / 44.1K',
    badge: 'CD HI-FI',
    desc: 'Эталонное качество Red Book Audio с побитовой точностью',
  },
  {
    id: 'high' as StreamQualityPref,
    label: 'Высокое качество (320 кбит/с AAC — экономия трафика)',
    shortLabel: '320K AAC',
    badge: 'AAC 320',
    desc: 'Оптимизированный битрейт для мобильного интернета и экономии батареи',
  },
];

export const THEME_OPTIONS: Array<{ id: DesignTheme; label: string; desc: string; accent: string }> = [
  {
    id: 'obsidian-liquid',
    label: 'Liquid Glass (Apple)',
    desc: 'Премиальное матовое стекло, неоновый аквамарин и воздушная глубина',
    accent: '#38bdf8',
  },
  {
    id: 'nordic-monolith',
    label: 'Minimal Air (Google)',
    desc: 'Воздушный минимализм, чистые формы и мягкий терракотовый акцент',
    accent: '#ff4f18',
  },
  {
    id: 'analog-telemetry',
    label: 'Amber Studio',
    desc: 'Тёплый студийный янтарный свет, дымчатое стекло и прецизионный тракт',
    accent: '#f59e0b',
  },
];

class UserStore {
  public userScope: string = tg.getUserScope();
  public favorites: Track[] = [];
  public history: Track[] = [];
  public queue: Track[] = [];
  public recentSearches: string[] = [];
  public settings: UserSettings = { ...DEFAULT_SETTINGS };

  private listeners: Set<StoreListener> = new Set();
  private lastRecordedTrackId: string | null = null;
  private syncTimer: number | null = null;

  constructor() {
    this.purgeLegacyUnscopedKeys();
    this.loadState();
    this.applyTheme(this.settings.theme);
    tg.setHapticsEnabled(this.settings.hapticsEnabled);
    audioEngine.autoPlayNext = this.settings.autoPlayNext;
    audioEngine.restoreQueue(this.queue);
    this.setupAudioEngineSync();
    this.pullFromServer();
  }

  /**
   * Purges old un-namespaced localStorage keys so different accounts on the same
   * device can never inherit or share un-scoped data.
   */
  private purgeLegacyUnscopedKeys() {
    try {
      for (const k of LEGACY_UNSCOPED_KEYS) {
        localStorage.removeItem(k);
      }
    } catch {
      // ignore
    }
  }

  /**
   * Re-checks current Telegram user scope (e.g. after tg.init()) and reloads
   * isolated user data if the active user changed.
   */
  public rebindUserScope() {
    const nextScope = tg.getUserScope();
    if (nextScope !== this.userScope) {
      this.userScope = nextScope;
      this.lastRecordedTrackId = null;
      this.loadState();
      this.applyTheme(this.settings.theme);
      tg.setHapticsEnabled(this.settings.hapticsEnabled);
      audioEngine.autoPlayNext = this.settings.autoPlayNext;
      audioEngine.restoreQueue(this.queue);
      this.notify();
    }
    this.pullFromServer();
  }

  private loadState() {
    const scope = this.userScope;

    try {
      const favRaw = localStorage.getItem(scopedKey(scope, 'favorites'));
      if (favRaw) {
        const parsed = JSON.parse(favRaw);
        this.favorites = Array.isArray(parsed)
          ? parsed.map(sanitizeTrackStream)
          : [];
      } else {
        this.favorites = [];
      }
    } catch (e) {
      console.warn('Failed to parse favorites from storage', e);
      this.favorites = [];
    }

    try {
      const histRaw = localStorage.getItem(scopedKey(scope, 'history'));
      if (histRaw) {
        const parsed = JSON.parse(histRaw);
        this.history = Array.isArray(parsed)
          ? parsed.map(sanitizeTrackStream)
          : [];
      } else {
        this.history = [];
      }
    } catch (e) {
      console.warn('Failed to parse history from storage', e);
      this.history = [];
    }

    try {
      const qRaw = localStorage.getItem(scopedKey(scope, 'queue'));
      if (qRaw) {
        const parsed = JSON.parse(qRaw);
        this.queue = Array.isArray(parsed)
          ? parsed.map(sanitizeTrackStream)
          : [];
      } else {
        this.queue = [];
      }
    } catch (e) {
      console.warn('Failed to parse queue from storage', e);
      this.queue = [];
    }

    try {
      const recRaw = localStorage.getItem(scopedKey(scope, 'recent_searches'));
      if (recRaw) {
        const parsed = JSON.parse(recRaw);
        this.recentSearches = Array.isArray(parsed) ? parsed : [];
      } else {
        this.recentSearches = [];
      }
    } catch (e) {
      console.warn('Failed to parse recent searches from storage', e);
      this.recentSearches = [];
    }

    try {
      const setRaw = localStorage.getItem(scopedKey(scope, 'settings'));
      if (setRaw) {
        this.settings = { ...DEFAULT_SETTINGS, ...JSON.parse(setRaw) };
      } else {
        this.settings = { ...DEFAULT_SETTINGS };
      }
    } catch (e) {
      console.warn('Failed to parse settings from storage', e);
      this.settings = { ...DEFAULT_SETTINGS };
    }
  }

  private scheduleServerSync() {
    if (typeof window === 'undefined') return;
    if (this.syncTimer) {
      window.clearTimeout(this.syncTimer);
    }
    this.syncTimer = window.setTimeout(() => {
      this.pushToServer();
    }, 350);
  }

  private async pullFromServer() {
    if (typeof window === 'undefined') return;
    const scopeAtStart = this.userScope;
    try {
      const resp = await fetch('/api/user/state', {
        method: 'GET',
        headers: {
          Accept: 'application/json',
          ...tg.getAuthHeaders(),
        },
        cache: 'no-store',
      });
      if (!resp.ok || scopeAtStart !== this.userScope) return;
      const data = await resp.json();
      if (!data || !data.isolatedScope) return;

      let changed = false;
      if (Array.isArray(data.favorites) && data.favorites.length > 0 && this.favorites.length === 0) {
        this.favorites = data.favorites.map(sanitizeTrackStream);
        localStorage.setItem(scopedKey(this.userScope, 'favorites'), JSON.stringify(this.favorites));
        changed = true;
      }
      if (Array.isArray(data.history) && data.history.length > 0 && this.history.length === 0) {
        this.history = data.history.map(sanitizeTrackStream);
        localStorage.setItem(scopedKey(this.userScope, 'history'), JSON.stringify(this.history));
        changed = true;
      }
      if (Array.isArray(data.queue) && data.queue.length > 0 && this.queue.length === 0) {
        this.queue = data.queue.map(sanitizeTrackStream);
        localStorage.setItem(scopedKey(this.userScope, 'queue'), JSON.stringify(this.queue));
        audioEngine.restoreQueue(this.queue);
        changed = true;
      }
      if (Array.isArray(data.recentSearches) && data.recentSearches.length > 0 && this.recentSearches.length === 0) {
        this.recentSearches = data.recentSearches;
        localStorage.setItem(scopedKey(this.userScope, 'recent_searches'), JSON.stringify(this.recentSearches));
        changed = true;
      }
      if (data.settings && typeof data.settings === 'object') {
        this.settings = { ...DEFAULT_SETTINGS, ...data.settings, ...this.settings };
        localStorage.setItem(scopedKey(this.userScope, 'settings'), JSON.stringify(this.settings));
      }
      if (changed) {
        this.notify();
      }
    } catch {
      // Offline or local dev without server state endpoint
    }
  }

  private async pushToServer() {
    if (typeof window === 'undefined') return;
    try {
      await fetch('/api/user/state', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...tg.getAuthHeaders(),
        },
        cache: 'no-store',
        body: JSON.stringify({
          favorites: this.favorites,
          history: this.history,
          queue: this.queue,
          recentSearches: this.recentSearches,
          settings: this.settings,
        }),
      });
    } catch {
      // Ignore transient network errors
    }
  }

  private saveFavorites() {
    try {
      localStorage.setItem(scopedKey(this.userScope, 'favorites'), JSON.stringify(this.favorites));
      this.scheduleServerSync();
    } catch (e) {
      console.warn('Failed to save favorites to storage', e);
    }
  }

  private saveHistory() {
    try {
      localStorage.setItem(scopedKey(this.userScope, 'history'), JSON.stringify(this.history));
      this.scheduleServerSync();
    } catch (e) {
      console.warn('Failed to save history to storage', e);
    }
  }

  private saveQueue() {
    try {
      localStorage.setItem(scopedKey(this.userScope, 'queue'), JSON.stringify(this.queue));
      this.scheduleServerSync();
    } catch (e) {
      console.warn('Failed to save queue to storage', e);
    }
  }

  private saveRecentSearches() {
    try {
      localStorage.setItem(scopedKey(this.userScope, 'recent_searches'), JSON.stringify(this.recentSearches));
      this.scheduleServerSync();
    } catch (e) {
      console.warn('Failed to save recent searches to storage', e);
    }
  }

  private saveSettings() {
    try {
      localStorage.setItem(scopedKey(this.userScope, 'settings'), JSON.stringify(this.settings));
      this.scheduleServerSync();
    } catch (e) {
      console.warn('Failed to save settings to storage', e);
    }
  }

  private notify() {
    this.listeners.forEach((cb) => {
      try {
        cb();
      } catch (err) {
        console.error('UserStore listener error', err);
      }
    });
  }

  public subscribe(cb: StoreListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  // Favorites Management
  public isFavorite(trackId: string): boolean {
    return this.favorites.some((t) => t.id === trackId);
  }

  public toggleFavorite(track: Track) {
    const exists = this.isFavorite(track.id);
    if (exists) {
      this.favorites = this.favorites.filter((t) => t.id !== track.id);
    } else {
      this.favorites = [track, ...this.favorites];
    }
    this.saveFavorites();
    this.notify();
  }

  public addFavorite(track: Track) {
    if (!this.isFavorite(track.id)) {
      this.favorites = [track, ...this.favorites];
      this.saveFavorites();
      this.notify();
    }
  }

  public removeFavorite(trackId: string) {
    if (this.isFavorite(trackId)) {
      this.favorites = this.favorites.filter((t) => t.id !== trackId);
      this.saveFavorites();
      this.notify();
    }
  }

  // History Management
  public addToHistory(track: Track) {
    if (!track || !track.id) return;
    this.lastRecordedTrackId = track.id;
    // Don't record duplicate if it is already top of history
    if (this.history.length > 0 && this.history[0].id === track.id) {
      return;
    }
    // Remove previous instance if it exists and place at the head (most recent)
    const filtered = this.history.filter((t) => t.id !== track.id);
    this.history = [track, ...filtered].slice(0, 30);
    this.saveHistory();
    this.notify();
  }

  public clearHistory() {
    this.history = [];
    this.lastRecordedTrackId = null;
    this.saveHistory();
    this.notify();
  }

  // Queue Management (Tracks actually played by the user)
  public addToPlayedQueue(track: Track) {
    if (!track || !track.id) return;
    const clean = sanitizeTrackStream(track);
    const existsIdx = this.queue.findIndex((t) => t.id === clean.id);
    if (existsIdx === -1) {
      this.queue = [...this.queue, clean].slice(-50);
    }
    this.saveQueue();
    audioEngine.syncQueue(this.queue);
    this.notify();
  }

  public setQueue(tracks: Track[]) {
    this.queue = Array.isArray(tracks)
      ? tracks.map(sanitizeTrackStream).slice(-50)
      : [];
    this.saveQueue();
    audioEngine.syncQueue(this.queue);
    this.notify();
  }

  public removeFromQueue(trackId: string) {
    this.queue = this.queue.filter((t) => t.id !== trackId);
    this.saveQueue();
    audioEngine.syncQueue(this.queue);
    this.notify();
  }

  public clearQueue() {
    this.queue = [];
    this.saveQueue();
    audioEngine.clearQueue();
    this.notify();
  }

  // Recent Searches Management
  public addRecentSearch(query: string) {
    const clean = query.trim();
    if (!clean || clean.length < 2) return;
    const filtered = this.recentSearches.filter(
      (s) => s.toLowerCase() !== clean.toLowerCase()
    );
    this.recentSearches = [clean, ...filtered].slice(0, 8);
    this.saveRecentSearches();
    this.notify();
  }

  public removeRecentSearch(query: string) {
    this.recentSearches = this.recentSearches.filter(
      (s) => s.toLowerCase() !== query.toLowerCase()
    );
    this.saveRecentSearches();
    this.notify();
  }

  public clearRecentSearches() {
    this.recentSearches = [];
    this.saveRecentSearches();
    this.notify();
  }

  // Settings Management
  public updateSettings(partial: Partial<UserSettings>) {
    this.settings = { ...this.settings, ...partial };

    if (partial.theme) {
      this.applyTheme(partial.theme);
    }

    if (partial.hapticsEnabled !== undefined) {
      tg.setHapticsEnabled(partial.hapticsEnabled);
    }

    if (partial.autoPlayNext !== undefined) {
      audioEngine.autoPlayNext = partial.autoPlayNext;
    }

    this.saveSettings();
    this.notify();
  }

  public applyTheme(theme: DesignTheme) {
    if (typeof document !== 'undefined') {
      document.documentElement.setAttribute('data-theme', theme);
    }
    switch (theme) {
      case 'obsidian-liquid':
        tg.setThemeColors('#090b10', '#05070a');
        break;
      case 'nordic-monolith':
        tg.setThemeColors('#f4f1ea', '#eae6dc');
        break;
      case 'analog-telemetry':
      default:
        tg.setThemeColors('#17120e', '#0e0b09');
        break;
    }
  }

  public applyEqPreset(preset: 'flat' | 'bass' | 'vocal' | 'warm' | 'custom') {
    const config = EQ_PRESETS[preset];
    if (preset !== 'custom' && config) {
      audioEngine.setBass(config.bass);
      audioEngine.setTreble(config.treble);
    }
    this.settings = { ...this.settings, eqPreset: preset };
    this.saveSettings();
    this.notify();
  }

  public resetSettings() {
    this.settings = { ...DEFAULT_SETTINGS };
    this.applyTheme(DEFAULT_SETTINGS.theme);
    tg.setHapticsEnabled(DEFAULT_SETTINGS.hapticsEnabled);
    audioEngine.autoPlayNext = DEFAULT_SETTINGS.autoPlayNext;
    audioEngine.setBass(0);
    audioEngine.setTreble(0);
    audioEngine.setBalance(0);
    this.saveSettings();
    this.notify();
  }

  private setupAudioEngineSync() {
    // Listen to track changes from audioEngine to record into history and played queue
    audioEngine.subscribe(() => {
      const current = audioEngine.currentTrack;
      if (current && current.id && current.id !== this.lastRecordedTrackId && audioEngine.isPlaying) {
        this.lastRecordedTrackId = current.id;
        this.addToHistory(current);
        this.addToPlayedQueue(current);
      }
    });
  }
}

export const userStore = new UserStore();
